import { Component, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { tokenDTO, suggestionDTO, editStateEnum, spellingStateEnum } from '@capiche/ts-data-models-shared';

import { CapicheMaterialModule } from 'capiche-material';
import { CapicheLoggerService } from 'capiche-logger';
import { DisclosurePathService, DisclosurePath } from 'capiche-disclosure-path';
import { CensorService, ReplacementService } from 'capiche-text-processor';

import { SuggestionComponent } from '../suggestion/suggestion.component';
import { ComponentInstanceTracker } from '../../utils/component-instance-tracker';

@Component({
  selector: 'app-word',
  standalone: true,
  imports: [CommonModule, CapicheMaterialModule, SuggestionComponent],
  templateUrl: './word.component.html',
  styleUrls: ['./word.component.css'],
})
export class WordComponent implements OnInit, OnChanges, OnDestroy {
  private disclosurePathService = inject(DisclosurePathService);
  private replacementService = inject(ReplacementService);
  private logger = new CapicheLoggerService('word.component:', false);
  private censorService = inject(CensorService);

  @Input({ required: true }) word!: tokenDTO;
  @Input({ required: true }) thisItemPath!: DisclosurePath;
  /** Editor instance identifier — passed through to ReplacementService. */
  @Input({ required: true }) elId!: string;
  @Input() panelSections: 'all' | 'headerOnly' | 'contentOnly' = 'all';

  childItemPaths: Record<number, DisclosurePath> = {};
  showAllSuggestions = false;

  get panelOpenState(): boolean {
    return this.disclosurePathService.isCurrent(this.thisItemPath);
  }

  constructor() {
    this.logger.log('constructor');
    ComponentInstanceTracker.increment('WordComponent');
  }

  ngOnInit() {
    this.logger.log('ngOnInit', this.word);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['word']) {
      const suggestions = this.processedSuggestions;
      this.childItemPaths = suggestions.reduce<Record<number, DisclosurePath>>((acc, s, i) => {
        const key = s.rank ?? i;
        acc[key] = this.buildChildItemPath(key);
        return acc;
      }, {});
    }
  }

  ngOnDestroy() {
    this.logger.log('ngOnDestroy');
    ComponentInstanceTracker.decrement('WordComponent');
  }

  private buildChildItemPath(index: number): DisclosurePath {
    return {
      ...this.thisItemPath,
      suggestionIndex: index,
    };
  }

  // return true if component should be displayed
  get showComponent(): boolean {
    if (!this.word) return false;
    this.logger.log('showComponent check for word:', this.word);
    const validEditStates: string[] = [editStateEnum.checked, editStateEnum.local, editStateEnum.discarded];
    if (!validEditStates.includes(this.word.editState)) return false;

    const spellingState = this.word.spellingState;
    return (
      (spellingState !== spellingStateEnum.correct &&
        spellingState !== spellingStateEnum.unknown &&
        spellingState !== spellingStateEnum.ignore) ||
      this.word.editState === editStateEnum.discarded
    );
  }

  onOpened() {
    this.disclosurePathService.setCurrent(this.thisItemPath);
  }

  onClosed() {
    // Only clear if this is *still* current path
    if (this.disclosurePathService.isCurrent(this.thisItemPath)) {
      this.disclosurePathService.setCurrent({
        ...this.thisItemPath,
        wordIndex: null,
        suggestionIndex: null,
      });
    }
  }

  /** Handle replacement emitted by child SuggestionComponent */
  onSuggestionReplacement(event: { path: DisclosurePath; suggestion: suggestionDTO }): void {
    this.logger.log('onSuggestionReplacement:', event.path, this.word);
    const path: DisclosurePath = event.path;
    const replacement: string | undefined =
      path.suggestionIndex != null ? this.word.suggestions?.[path.suggestionIndex]?.word : undefined;

    if (path.paragraphIndex == null || path.sentenceIndex == null || path.wordIndex == null || !replacement) {
      this.logger.warn('onSuggestionReplacement: incomplete path or missing replacement', { path });
      return;
    }
    this.replacementService.makeReplacement(this.elId, path.paragraphIndex, path.sentenceIndex, path.wordIndex, replacement);
  }

  get processedSuggestions(): suggestionDTO[] {
    const censored = this.censorService.censorSuggestionDTO(this.word.suggestions ?? []);
    return censored
      .sort((a, b) => {
        if (a.rank == null) return -1;
        if (b.rank == null) return 1;
        return a.rank - b.rank;
      })
      .slice(0, 10);
  }

  private readonly SUGGESTION_DEFAULT_LIMIT = 5;

  /** Suggestions shown in the panel — first 5 by default, all when expanded. */
  get visibleSuggestions(): suggestionDTO[] {
    return this.showAllSuggestions
      ? this.processedSuggestions
      : this.processedSuggestions.slice(0, this.SUGGESTION_DEFAULT_LIMIT);
  }

  /** True when there are hidden suggestions the user can reveal. */
  get hasMoreSuggestions(): boolean {
    return !this.showAllSuggestions && this.processedSuggestions.length > this.SUGGESTION_DEFAULT_LIMIT;
  }

  /** Number of suggestions hidden behind the "show more" affordance. */
  get hiddenCount(): number {
    return this.processedSuggestions.length - this.SUGGESTION_DEFAULT_LIMIT;
  }

  toggleShowAll(): void {
    this.showAllSuggestions = true;
  }

  trackByFn(index: number, s: suggestionDTO): number {
    return s.rank ?? index;
  }
}
